import { useState, useEffect, useRef, createContext } from "react";
import { useNavigate } from "react-router";
import { notification } from "antd";
import {
	Box,
	Checkbox,
	Divider,
	InputAdornment,
	ListSubheader,
	MenuItem,
	Pagination,
	Select,
	TextField,
	Typography,
	Button,
	IconButton,
	Stack,
} from "@mui/material";
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	Search,
	X,
} from "lucide-react";

import ExampleImage from "../../../src/assets/success-cases-example.jpg";
import InfoSuccessCases from "../InfoSuccessCases/InfoSuccessCases";
import hospitalsServices from "../../services/hospitalsServices";
import ideasServices from "../../services/ideasServices";
import ReachLogo from "../../assets/Logos/reach_logo.png";
import "./SuccessCases.scss";

export const SuccessCasesContext = createContext<any>({
	setOpenIdea: () => {},
	upvote: () => {},
	deleteVote: () => {},
});

export default function SuccessCases() {
	const [selectedHospitals, setSelectedHospitals] = useState<any>([]);
	const [areFiltersVisible, setAreFiltersVisible] = useState(true);
	const [searchHospitalText, setSearchHospitalText] = useState("");
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [hospitals, setHospitals] = useState<any>([]);
	const containerRef = useRef<HTMLDivElement>(null);
	const [ideas, setIdeas] = useState<any[]>([]);
	const [openIdea, setOpenIdea] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedIdea, setSelectedIdea] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [query, setQuery] = useState("");

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			await ideasServices.getSuccessCases().then((res) => setIdeas(res));
			await hospitalsServices
				.getAllActiveHospitals()
				.then((res) => setHospitals(res));
			setIsLoading(false);
		};
		fetchData();
	}, []);

	const upvote = async (ideaId: number) => {
		ideasServices.upVote(ideaId).then(() =>
			notification.success({
				message: "¡Muchas gracias por tu voto!",
			}),
		);
	};

	const deleteVote = async (ideaId: number) => {
		ideasServices.deleteVote(ideaId).then(() =>
			notification.success({
				message: "¡Se eliminó tu voto!",
			}),
		);
	};

	useEffect(() => {
		checkScroll();
	}, [selectedHospitals]);

	const changeSelectedHospitals = (hospital: any) => {
		setSelectedHospitals((prev: any[]) => {
			const exists = prev.includes(hospital);
			if (exists) {
				return prev.filter((h) => h.id !== hospital.id);
			} else {
				return [...prev, hospital];
			}
		});
	};

	const handleSearchHospitalChange = (event: any) => {
		setSearchHospitalText(event.target.value.toLowerCase());
	};

	const handlePageChange = (event: any, value: any) => {
		setCurrentPage(value);
	};

	const checkScroll = () => {
		if (!containerRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

		setCanScrollLeft(scrollLeft > 0);
		setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
	};

	const handleScroll = (direction: "left" | "right") => {
		if (!containerRef.current) return;

		const scrollAmount = containerRef.current.clientWidth;

		if (direction === "left") {
			containerRef.current.scrollBy({
				left: -scrollAmount,
				behavior: "smooth",
			});
		} else {
			containerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
		}

		setTimeout(checkScroll, 150);
	};

	const filteredHospitals = hospitals.filter((hospital: any) =>
		hospital.hospital_name.toLowerCase().includes(searchHospitalText),
	);

	const handleSelectIdea = (idea: any) => {
		setSelectedIdea(idea);
		setOpenIdea(true);
	};

	const filteredIdeas = ideas.filter(
		(idea: any) =>
			idea.title.toLowerCase().includes(query.toLowerCase()) &&
			(selectedHospitals.length === 0 ||
				selectedHospitals.some((hospital: any) =>
					idea.hospital_area.includes(hospital.hospital_name),
				)),
	);

	const ITEMS_PER_PAGE = 9;
	const startPageIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endPageIndex = startPageIndex + ITEMS_PER_PAGE;
	const paginatedIdeas = filteredIdeas.slice(startPageIndex, endPageIndex);
	const rows = paginatedIdeas.length / 3 + (paginatedIdeas.length % 3 === 0 ? 0 : 1);

	const navigator = useNavigate();

	return (
		<Box className="cases">
			{!openIdea ? (
				<>
					<Box className="cases__header">
						<Box className="cases__header__grain" />

						<Button
							className="cases__header__button"
							onClick={() => navigator("/")}
						>
							<ChevronLeft />
							<Typography>Regresar</Typography>
						</Button>

						<Stack justifyContent={"center"}>
							<Typography className="cases__header__title">
								Casos de éxito
							</Typography>
							<Typography className="cases__header__subtitle">
								Conoce las ideas que se hicieron realidad y están
								transformando nuestra red hospitalaria.
							</Typography>
						</Stack>

						<img
							src={ReachLogo}
							alt=""
						/>
					</Box>

					<Box className="cases__content">
						<Box className="cases__content__search-column">
							<Box className="cases__content__search-column__row">
								<TextField
									size="medium"
									className="cases__content__search-column__row-text-field"
									type="text"
									placeholder="Buscar"
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Search className="cases__content__search-column__row-text-field__icon" />
												</InputAdornment>
											),
											endAdornment: (
												<IconButton
													onClick={() => {
														if (areFiltersVisible === false) {
															setAreFiltersVisible(true);
														} else {
															setAreFiltersVisible(false);
														}
													}}
													disableRipple
												>
													{areFiltersVisible ? (
														<ChevronUp className="header__searching__search-bar__collapse-icon" />
													) : (
														<ChevronDown className="header__searching__search-bar__collapse-icon" />
													)}
												</IconButton>
											),
										},
									}}
									onChange={(e) => setQuery(e.target.value)}
								/>
							</Box>
							<Divider sx={{ color: "#EDEDED" }} />
							{areFiltersVisible && (
								<Box className="cases__content__search-column__filters">
									{canScrollLeft && (
										<Button
											className="cases__content__search-column__filters-leftarrow"
											onClick={() => handleScroll("left")}
										>
											<ChevronLeft />
										</Button>
									)}
									<Select
										id="hospital-selector"
										value={"Unidad"}
										IconComponent={ChevronDown}
										className={
											isOpen
												? "cases__content__search-column__filters__select--opened"
												: "cases__content__search-column__filters__select"
										}
										renderValue={() => (
											<Box className="cases__content__search-column__filters__select-title">
												<Typography>Unidad</Typography>
												<ChevronDown className="cases__content__search-column__filters__select-title__icon" />
											</Box>
										)}
										size="small"
										displayEmpty
										open={isOpen}
										onOpen={() => setIsOpen(true)}
										onClose={() => setIsOpen(false)}
										MenuProps={{
											anchorOrigin: {
												vertical: "bottom",
												horizontal: "left",
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left",
											},
											PaperProps: {
												sx: {
													maxHeight: 350,
													borderRadius: "12px",
													marginTop: "6px",
												},
											},
										}}
									>
										<ListSubheader className="cases__content__search-column__filters__select__header">
											<Typography className="cases__content__search-column__filters__select__header-title">
												Unidades
											</Typography>
											<Typography
												className="cases__content__search-column__filters__select__header-button"
												onClick={() => setSelectedHospitals([])}
											>
												Limpiar todo
											</Typography>
										</ListSubheader>
										<ListSubheader>
											<TextField
												size="small"
												className="cases__content__search-column__filters__select-text-field"
												type="text"
												placeholder="Buscar"
												slotProps={{
													input: {
														startAdornment: (
															<InputAdornment position="start">
																<Search
																	size={20}
																	color="#BEBEBE"
																/>
															</InputAdornment>
														),
													},
												}}
												onChange={handleSearchHospitalChange}
											/>
										</ListSubheader>
										<ListSubheader className="cases__content__search-column__filters__select__hospitals">
											{filteredHospitals.map((hospital: any) => (
												<MenuItem
													key={hospital.id}
													value={hospital.hospital_name}
													sx={{
														py: 0,
														px: 1.5,
													}}
													onClick={() =>
														changeSelectedHospitals(hospital)
													}
												>
													<Checkbox
														className="cases__content__search-column__filters__select__hospitals-checkbox"
														checked={selectedHospitals.includes(
															hospital,
														)}
													/>
													<Typography className="cases__content__search-column__filters__select__hospitals-name">
														{hospital.hospital_name}
													</Typography>
												</MenuItem>
											))}
										</ListSubheader>
									</Select>
									<Box
										ref={containerRef}
										className="cases__content__search-column__filters-box"
									>
										{selectedHospitals.map((hospital: any) => (
											<Box className="cases__content__search-column__filters__labels">
												<Typography className="cases__content__search-column__filters__labels-text">
													{hospital.hospital_name}
												</Typography>
												<X
													className="cases__content__search-column__filters__labels-button"
													onClick={() =>
														changeSelectedHospitals(hospital)
													}
												/>
											</Box>
										))}
									</Box>
									{canScrollRight && (
										<Button
											onClick={() => handleScroll("right")}
											className="cases__content__search-column__filters-rightarrow"
										>
											<ChevronRight />
										</Button>
									)}
								</Box>
							)}
						</Box>
						<Box className="cases__content__ideas">
							{Array.from({ length: rows }).map((_, rowIndex) => (
								<Box
									key={rowIndex}
									className="cases__content__ideas__row"
								>
									{paginatedIdeas
										.slice(rowIndex * 3, rowIndex * 3 + 3)
										.map((idea) => (
											<Box
												onClick={() => handleSelectIdea(idea)}
												className="cases__content__ideas__row__item"
											>
												<img
													className="cases__content__ideas__row__item-image"
													src={idea.image_url ?? ExampleImage}
													alt=""
												/>
												<Box className="cases__content__ideas__row__item__info">
													<Typography className="cases__content__ideas__row__item__info-hospital">
														CHRISTUS MUGUERZA{" "}
														{idea.hospital_area}
													</Typography>
													<Typography className="cases__content__ideas__row__item__info-title">
														{idea.title}
													</Typography>
													<Typography className="cases__content__ideas__row__item__info-description">
														{idea.description}
													</Typography>
												</Box>
											</Box>
										))}
								</Box>
							))}
							<Pagination
								className="cases__content__ideas-pagination"
								count={Math.ceil(filteredIdeas.length / ITEMS_PER_PAGE)}
								page={currentPage}
								onChange={handlePageChange}
								variant="outlined"
								shape="rounded"
							/>
						</Box>
					</Box>
				</>
			) : (
				<SuccessCasesContext.Provider
					value={{
						setOpenIdea,
						upvote,
						deleteVote,
					}}
				>
					<InfoSuccessCases idea={selectedIdea} />
				</SuccessCasesContext.Provider>
			)}
			{isLoading && (
				<Box className="loader-overlay">
					<Box className="loader-container">
						<Box className="custom-loader" />
					</Box>
				</Box>
			)}
		</Box>
	);
}
